// components/Canvas.tsx
import React, { DragEvent, useState } from 'react';
import styled from 'styled-components';
// import TopLevelElement from './TopLevelElement';
import SectionContainer from './SectionContainer';
import { CanvasItem, Element, Section, TempSettings } from '../../types/types';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';
import StyledText from '../StyledComponents/StyledText';
import PageDesignDropzoneIcon from '../../assets/icons/PageDesignDropzoneIcon';
import { getElementDefaultSettings, createElementWithDefaults, getSectionDefaultProperties } from '../../utils/pageElementDefaultSettings';
import { createRuntimeElementId, createStableElementClass } from '../../utils/pageBuilderElementClass';
import { hexToRgba } from '../../utils/helper';

const CanvasWrapper = styled.div<{ theme: Theme }>`
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  border-radius: 8px;
  min-height: 500px;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
  padding-top:35px;
`;

const CanvasContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const BottomDropZone = styled.div<{ isOver?: boolean, theme: Theme }>`
  background: ${props => props.isOver ? hexToRgba(props.theme.CorporateTheme.colors.blueberry, 0.07) : props.theme.CorporateTheme.colors.filterBG};
  border: ${props => props.isOver ? `1px dashed ${props.theme.CorporateTheme.colors.blueberry}` : `1px dashed ${props.theme.CreateFormTheme.colors.inputBorders}`};
  border-radius: 4px;
  padding: 60px 20px;
  height: 277px;
  transition: all 0.3s;
  text-align: center;
  color: #adb5bd;
  margin-top: 15px;
  .icon {
    font-size: 80px;
    margin-bottom: 20px;
    opacity: 0.5;
  }
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
  }
  
  p {
    font-size: 13px;
    margin: 0;
  }
`;

const InsertDropZone = styled.div<{ theme: Theme; $isActive: boolean; $isVisible: boolean }>`
    height: ${({ $isVisible, $isActive }) => ($isVisible ? ($isActive ? '50px' : '50px') : '12px')};
    margin: 6px 0 6px 0;
    border-radius: 6px;
    border: ${({ $isVisible, $isActive, theme }) => {
                if (!$isVisible) return '1px dashed transparent';
                if ($isActive) return `1px solid ${theme.CorporateTheme.colors.blueberry}`;
                return `1px dashed ${theme.CreateFormTheme.colors.inputBorders}`;
        }};
    background: ${({ $isVisible, $isActive, theme }) => {
                if (!$isVisible) return 'transparent';
                if ($isActive) return hexToRgba(theme.CorporateTheme.colors.blueberry, 0.08);
                return theme.CorporateTheme.colors.filterBG;
        }};
    transition: all 0.2s ease;
`;

interface CanvasProps {
    canvasItems: CanvasItem[];
    setCanvasItems: (items: CanvasItem[]) => void;
    draggedElement: string | null;
    selectedElement: string | null;
    setSelectedElement: (id: string | null) => void;
    setDraggedElement: (element: string | null) => void;
    openSettingsPanel: (config: any, currentSettings?: TempSettings) => void;
    tempSettings: TempSettings;
    pendingChanges?: Map<string, TempSettings>;
    setPendingChanges?: React.Dispatch<React.SetStateAction<Map<string, TempSettings>>>;
    pageElementsData?: any;
    primaryFont?: string;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

type SectionColumn = 'single' | 'left' | 'right';

type DraggedCanvasItem =
    | { kind: 'section'; sectionId: string }
    | { kind: 'element'; sectionId: string; column: SectionColumn; elementId: string; sourceIndex: number };

interface ElementDropZoneState {
    sectionId: string;
    column: SectionColumn;
    index: number;
}

const Canvas: React.FC<CanvasProps> = ({
    canvasItems,
    setCanvasItems,
    draggedElement,
    selectedElement,
    setSelectedElement,
    setDraggedElement,
    openSettingsPanel,
    tempSettings,
    pendingChanges = new Map(),
    setPendingChanges,
    pageElementsData,
    primaryFont,
    scrollContainerRef
}) => {
    const { theme } = useTheme();
    const [isOverCanvas, setIsOverCanvas] = useState(false);
    const [activeInsertIndex, setActiveInsertIndex] = useState<number | null>(null);
    const [activeElementDropZone, setActiveElementDropZone] = useState<ElementDropZoneState | null>(null);
    const [draggedCanvasItem, setDraggedCanvasItem] = useState<DraggedCanvasItem | null>(null);
    const [isOverSection, setIsOverSection] = useState<string | null>(null);
    const [isOverColumn, setIsOverColumn] = useState<{ sectionId: string; column: 'left' | 'right' } | null>(null);

    const clonePendingSettingsWithMeta = (
        settings: any,
        overrides: {
            itemId?: string;
            sectionId?: string;
            column?: 'left' | 'right' | 'single' | undefined;
            type?: string;
            isInnerElement?: boolean;
        }
    ) => {
        const clonedSettings = JSON.parse(JSON.stringify(settings || {}));
        const existingMeta = clonedSettings?._meta || {};

        const nextMeta = {
            ...existingMeta,
            ...overrides
        };

        if (overrides.column !== undefined) {
            nextMeta.column = overrides.column;
        }

        if (overrides.isInnerElement !== undefined) {
            nextMeta.isInnerElement = overrides.isInnerElement;
        }

        clonedSettings._meta = nextMeta;
        return clonedSettings;
    };

    const buildCanvasItemFromDragged = (): Section | null => {
        if (!draggedElement) return null;

        if (draggedElement === 'single_column' || draggedElement === 'multi_column') {
            return {
                id: `section-${Date.now()}`,
                type: draggedElement as 'single_column' | 'multi_column',
                elements: [],
                properties: getSectionDefaultProperties(draggedElement as 'single_column' | 'multi_column'),
                ...(draggedElement === 'multi_column' && {
                    columns: {
                        left: [],
                        right: []
                    }
                })
            };
        }

        const elementDefaults = createElementWithDefaults(draggedElement, pageElementsData?.templates);
        const runtimeId = createRuntimeElementId(draggedElement);
        const newElement: Element = {
            id: runtimeId,
            type: draggedElement as Element['type'],
            content: elementDefaults.content,
            properties: {
                ...(elementDefaults.properties || {}),
                elementClass: createStableElementClass(draggedElement, runtimeId)
            }
        };

        return {
            id: `section-${Date.now()}`,
            type: 'single_column',
            elements: [newElement],
            properties: getSectionDefaultProperties('single_column')
        };
    };

    const handleDropAtIndex = (e: DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.stopPropagation();

        if (draggedCanvasItem?.kind === 'section') {
            const fromIndex = canvasItems.findIndex(item => item.id === draggedCanvasItem.sectionId);
            if (fromIndex === -1) return;

            const movingSection = canvasItems[fromIndex];
            const nextItems = canvasItems.filter(item => item.id !== draggedCanvasItem.sectionId);
            const insertionIndex = fromIndex < index ? index - 1 : index;
            nextItems.splice(insertionIndex, 0, movingSection);

            setCanvasItems(nextItems);
            setSelectedElement(movingSection.id);
            setDraggedCanvasItem(null);
            setActiveInsertIndex(null);
            setActiveElementDropZone(null);
            return;
        }

        if (draggedCanvasItem?.kind === 'element') {
            return;
        }

        const newItem = buildCanvasItemFromDragged();
        if (!newItem) return;

        const nextItems = [...canvasItems];
        nextItems.splice(index, 0, newItem);
        setCanvasItems(nextItems);

        setActiveInsertIndex(null);
        setIsOverCanvas(false);
        setActiveElementDropZone(null);
        setDraggedElement(null);
        setDraggedCanvasItem(null);
    };

    const handleCanvasDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOverCanvas(false);
        setActiveInsertIndex(null);

        if (draggedCanvasItem?.kind === 'section') {
            const fromIndex = canvasItems.findIndex(item => item.id === draggedCanvasItem.sectionId);
            if (fromIndex === -1) return;

            const movingSection = canvasItems[fromIndex];
            const nextItems = canvasItems.filter(item => item.id !== draggedCanvasItem.sectionId);
            nextItems.push(movingSection);

            setCanvasItems(nextItems);
            setSelectedElement(movingSection.id);
            setDraggedCanvasItem(null);
            setActiveElementDropZone(null);
            return;
        }

        if (draggedCanvasItem?.kind === 'element') {
            return;
        }

        const newItem = buildCanvasItemFromDragged();
        if (!newItem) return;

        setCanvasItems([...canvasItems, newItem]);

        setActiveElementDropZone(null);
        setDraggedElement(null);
        setDraggedCanvasItem(null);
    };

    const handleSectionDrop = (e: DragEvent<HTMLDivElement>, sectionId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOverSection(null);
        setActiveInsertIndex(null);
        setActiveElementDropZone(null);

        if (!draggedElement || draggedElement === 'single_column' || draggedElement === 'multi_column') return;

        const elementDefaults = createElementWithDefaults(draggedElement, pageElementsData?.templates);
        const runtimeId = createRuntimeElementId(draggedElement);
        const newElement: Element = {
            id: runtimeId,
            type: draggedElement as Element['type'],
            content: elementDefaults.content,
            properties: {
                ...(elementDefaults.properties || {}),
                elementClass: createStableElementClass(draggedElement, runtimeId)
            }
        };

        setCanvasItems(canvasItems.map(item => {
            if ('type' in item && item.id === sectionId && item.type === 'single_column') {
                const section = item as Section;
                return { ...section, elements: [...section.elements, newElement] };
            }
            return item;
        }));

        setDraggedElement(null);
    };

    const handleColumnDrop = (e: DragEvent<HTMLDivElement>, sectionId: string, column: 'left' | 'right') => {
        e.preventDefault();
        e.stopPropagation();
        setIsOverColumn(null);
        setActiveInsertIndex(null);
        setActiveElementDropZone(null);

        if (!draggedElement || draggedElement === 'single_column' || draggedElement === 'multi_column') return;

        const elementDefaults = createElementWithDefaults(draggedElement, pageElementsData?.templates);
        const runtimeId = createRuntimeElementId(draggedElement);
        const newElement: Element = {
            id: runtimeId,
            type: draggedElement as Element['type'],
            content: elementDefaults.content,
            properties: {
                ...(elementDefaults.properties || {}),
                elementClass: createStableElementClass(draggedElement, runtimeId)
            }
        };

        setCanvasItems(canvasItems.map(item => {
            if ('type' in item && item.id === sectionId && item.type === 'multi_column') {
                const section = item as Section & { columns: Record<string, Element[]> };
                if (!section.columns) return item;
                return {
                    ...section,
                    columns: {
                        ...section.columns,
                        [column]: [...section.columns[column], newElement]
                    }
                };
            }
            return item;
        }));

        setDraggedElement(null);
    };

    const handleDeleteTopLevel = (itemId: string) => {
        setCanvasItems(canvasItems.filter(item => item.id !== itemId));
        setSelectedElement(null);
    };

    const handleDuplicateSection = (sectionId: string) => {
        const itemIndex = canvasItems.findIndex(item => item.id === sectionId);
        if (itemIndex === -1) return;

        const item = canvasItems[itemIndex];
        if (!('type' in item) || (item.type !== 'single_column' && item.type !== 'multi_column')) return;

        const section = item as Section;
        const newSectionId = `section-${Date.now()}`;
        
        // Map old element IDs to new element IDs for pending changes
        const elementIdMap = new Map<string, string>();
        const elementTypeMap = new Map<string, string>();
        
        const clonedElements = section.elements.map(el => {
            const newId = createRuntimeElementId(el.type);
            elementIdMap.set(el.id, newId);
            elementTypeMap.set(el.id, el.type);
            return {
                ...el,
                id: newId,
                properties: {
                    ...(el.properties || {}),
                    elementClass: createStableElementClass(el.type, newId)
                }
            };
        });

        const clonedColumns = section.columns ? {
            left: section.columns.left.map(el => {
                const newId = createRuntimeElementId(el.type);
                elementIdMap.set(el.id, newId);
                elementTypeMap.set(el.id, el.type);
                return {
                    ...el,
                    id: newId,
                    properties: {
                        ...(el.properties || {}),
                        elementClass: createStableElementClass(el.type, newId)
                    }
                };
            }),
            right: section.columns.right.map(el => {
                const newId = createRuntimeElementId(el.type);
                elementIdMap.set(el.id, newId);
                elementTypeMap.set(el.id, el.type);
                return {
                    ...el,
                    id: newId,
                    properties: {
                        ...(el.properties || {}),
                        elementClass: createStableElementClass(el.type, newId)
                    }
                };
            })
        } : undefined;

        const cloned: Section = {
            ...section,
            id: newSectionId,
            elements: clonedElements,
            columns: clonedColumns,
            properties: section.properties ? JSON.parse(JSON.stringify(section.properties)) : undefined
        };

        // Duplicate pending changes for section and all its elements
        if (setPendingChanges) {
            setPendingChanges(prev => {
                const newChanges = new Map(prev);
                
                // Duplicate section properties if they exist
                if (prev.has(sectionId)) {
                    const sectionSettings = prev.get(sectionId);
                    if (sectionSettings) {
                        newChanges.set(
                            newSectionId,
                            clonePendingSettingsWithMeta(sectionSettings, {
                                itemId: newSectionId,
                                type: 'section',
                                isInnerElement: false
                            })
                        );
                    }
                }

                // Duplicate element properties
                elementIdMap.forEach((newId, oldId) => {
                    const elementType = elementTypeMap.get(oldId) || oldId.split('-')[0];

                    // For single-column elements
                    if (prev.has(oldId)) {
                        const settings = prev.get(oldId);
                        if (settings) {
                            const clonedSettings = clonePendingSettingsWithMeta(settings, {
                                itemId: newId,
                                type: 'element',
                                isInnerElement: false
                            });
                            clonedSettings.elementClass = createStableElementClass(elementType, newId);
                            newChanges.set(newId, clonedSettings);
                        }
                    }

                    // For single-column elements (section scoped key)
                    const singleKey = `${sectionId}-single-${oldId}`;
                    if (prev.has(singleKey)) {
                        const settings = prev.get(singleKey);
                        if (settings) {
                            const clonedSettings = clonePendingSettingsWithMeta(settings, {
                                itemId: newId,
                                sectionId: newSectionId,
                                column: 'single',
                                type: 'column-element',
                                isInnerElement: true
                            });
                            clonedSettings.elementClass = createStableElementClass(elementType, newId);
                            newChanges.set(`${newSectionId}-single-${newId}`, clonedSettings);
                        }
                    }
                    
                    // For multi-column elements (left)
                    const leftKey = `${sectionId}-left-${oldId}`;
                    if (prev.has(leftKey)) {
                        const settings = prev.get(leftKey);
                        if (settings) {
                            const clonedSettings = clonePendingSettingsWithMeta(settings, {
                                itemId: newId,
                                sectionId: newSectionId,
                                column: 'left',
                                type: 'column-element',
                                isInnerElement: true
                            });
                            clonedSettings.elementClass = createStableElementClass(elementType, newId);
                            newChanges.set(`${newSectionId}-left-${newId}`, clonedSettings);
                        }
                    }
                    
                    // For multi-column elements (right)
                    const rightKey = `${sectionId}-right-${oldId}`;
                    if (prev.has(rightKey)) {
                        const settings = prev.get(rightKey);
                        if (settings) {
                            const clonedSettings = clonePendingSettingsWithMeta(settings, {
                                itemId: newId,
                                sectionId: newSectionId,
                                column: 'right',
                                type: 'column-element',
                                isInnerElement: true
                            });
                            clonedSettings.elementClass = createStableElementClass(elementType, newId);
                            newChanges.set(`${newSectionId}-right-${newId}`, clonedSettings);
                        }
                    }
                });

                return newChanges;
            });
        }

        const nextItems = [...canvasItems];
        nextItems.splice(itemIndex + 1, 0, cloned);
        setCanvasItems(nextItems);
        setSelectedElement(cloned.id);
    };

    /*
    const handleDuplicateTopLevel = (itemId: string) => {
        const itemIndex = canvasItems.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return;

        const item = canvasItems[itemIndex];
        if (!('type' in item) || item.type === 'single_column' || item.type === 'multi_column') return;

        const element = item as Element;
        const newId = `${element.type}-${Date.now()}`;
        const cloned: Element = {
            ...element,
            id: newId,
            properties: element.properties ? JSON.parse(JSON.stringify(element.properties)) : undefined
        };

        // Duplicate pending changes for this element
        if (setPendingChanges && pendingChanges.has(itemId)) {
            const originalSettings = pendingChanges.get(itemId);
            if (originalSettings) {
                setPendingChanges(prev => {
                    const newChanges = new Map(prev);
                    newChanges.set(newId, { ...originalSettings });
                    return newChanges;
                });
            }
        }

        const nextItems = [...canvasItems];
        nextItems.splice(itemIndex + 1, 0, cloned);
        setCanvasItems(nextItems);
        setSelectedElement(cloned.id);
    };
    */

    const handleDeleteFromSection = (sectionId: string, elementId: string) => {
        setCanvasItems(canvasItems.map(item => {
            if ('type' in item && item.id === sectionId && item.type === 'single_column') {
                const section = item as Section;
                return { ...section, elements: section.elements.filter((el: Element) => el.id !== elementId) };
            }
            return item;
        }));
        setSelectedElement(null);
    };

    const handleDuplicateFromSection = (sectionId: string, elementId: string) => {
        setCanvasItems(canvasItems.map(item => {
            if ('type' in item && item.id === sectionId && item.type === 'single_column') {
                const section = item as Section;
                const elementIndex = section.elements.findIndex((el: Element) => el.id === elementId);
                if (elementIndex === -1) return item;
                const element = section.elements[elementIndex];
                const newId = createRuntimeElementId(element.type);
                const cloned: Element = {
                    ...element,
                    id: newId,
                    properties: {
                        ...(element.properties ? JSON.parse(JSON.stringify(element.properties)) : {}),
                        elementClass: createStableElementClass(element.type, newId)
                    }
                };

                // Duplicate pending changes for this element in section
                if (setPendingChanges) {
                    setPendingChanges(prev => {
                        const newChanges = new Map(prev);

                        const baseKey = elementId;
                        if (prev.has(baseKey)) {
                            const originalSettings = prev.get(baseKey);
                            if (originalSettings) {
                                const clonedSettings = clonePendingSettingsWithMeta(originalSettings, {
                                    itemId: newId,
                                    type: 'element',
                                    isInnerElement: false
                                });
                                clonedSettings.elementClass = createStableElementClass(element.type, newId);
                                newChanges.set(newId, clonedSettings);
                            }
                        }

                        const sectionScopedKey = `${sectionId}-single-${elementId}`;
                        if (prev.has(sectionScopedKey)) {
                            const originalSettings = prev.get(sectionScopedKey);
                            if (originalSettings) {
                                const clonedSettings = clonePendingSettingsWithMeta(originalSettings, {
                                    itemId: newId,
                                    sectionId,
                                    column: 'single',
                                    type: 'column-element',
                                    isInnerElement: true
                                });
                                clonedSettings.elementClass = createStableElementClass(element.type, newId);
                                newChanges.set(`${sectionId}-single-${newId}`, clonedSettings);
                            }
                        }

                        return newChanges;
                    });
                }

                const nextElements = [...section.elements];
                nextElements.splice(elementIndex + 1, 0, cloned);
                setSelectedElement(cloned.id);
                return { ...section, elements: nextElements };
            }
            return item;
        }));
    };

    const handleDeleteFromColumn = (sectionId: string, column: 'left' | 'right', elementId: string) => {
        setCanvasItems(canvasItems.map(item => {
            if ('type' in item && item.id === sectionId && item.type === 'multi_column') {
                const section = item as Section & { columns: Record<string, Element[]> };
                if (!section.columns) return item;
                return {
                    ...section,
                    columns: {
                        ...section.columns,
                        [column]: section.columns[column].filter((el: Element) => el.id !== elementId)
                    }
                };
            }
            return item;
        }));
        setSelectedElement(null);
    };

    const handleDuplicateFromColumn = (sectionId: string, column: 'left' | 'right', elementId: string) => {
        setCanvasItems(canvasItems.map(item => {
            if ('type' in item && item.id === sectionId && item.type === 'multi_column') {
                const section = item as Section & { columns: Record<string, Element[]> };
                if (!section.columns) return item;
                const columnElements = section.columns[column];
                const elementIndex = columnElements.findIndex((el: Element) => el.id === elementId);
                if (elementIndex === -1) return item;
                const element = columnElements[elementIndex];
                const newId = createRuntimeElementId(element.type);
                const cloned: Element = {
                    ...element,
                    id: newId,
                    properties: {
                        ...(element.properties ? JSON.parse(JSON.stringify(element.properties)) : {}),
                        elementClass: createStableElementClass(element.type, newId)
                    }
                };

                // Duplicate pending changes for this column element
                const originalKey = `${sectionId}-${column}-${elementId}`;
                const newKey = `${sectionId}-${column}-${newId}`;
                if (setPendingChanges && pendingChanges.has(originalKey)) {
                    const originalSettings = pendingChanges.get(originalKey);
                    if (originalSettings) {
                        setPendingChanges(prev => {
                            const newChanges = new Map(prev);
                            const clonedSettings = clonePendingSettingsWithMeta(originalSettings, {
                                itemId: newId,
                                sectionId,
                                column,
                                type: 'column-element',
                                isInnerElement: true
                            });
                            clonedSettings.elementClass = createStableElementClass(element.type, newId);
                            newChanges.set(newKey, clonedSettings);
                            return newChanges;
                        });
                    }
                }

                const nextColumnElements = [...columnElements];
                nextColumnElements.splice(elementIndex + 1, 0, cloned);
                setSelectedElement(cloned.id);
                return {
                    ...section,
                    columns: {
                        ...section.columns,
                        [column]: nextColumnElements
                    }
                };
            }
            return item;
        }));
    };

    const loadElementSettings = (config: any) => {
        let currentSettings: any = {};

        // Generate the change key to check for pending changes
        const changeKey = config.type === 'column-element' 
            ? `${config.sectionId}-${config.column}-${config.itemId}`
            : config.itemId;

        // Check if there are pending changes for this element
        const pendingChange = pendingChanges.get(changeKey);
        
        if (pendingChange) {
            // Load pending changes if they exist
            currentSettings = { ...pendingChange };
            delete currentSettings._meta; // Remove metadata before loading
        } else if (config.type === 'element') {
            const item = canvasItems.find(item => item.id === config.itemId);
            if (item && 'content' in item) {
                const element = item as Element;
                // Check if element has existing properties
                if (element.properties && Object.keys(element.properties).length > 0) {
                    currentSettings = {
                        content: element.content || '',
                        ...element.properties
                    };
                } else {
                    // Load default settings from API templates
                    const defaultSettings = getElementDefaultSettings(String(element.type), pageElementsData?.templates);
                    currentSettings = {
                        content: element.content || '',
                        ...defaultSettings
                    };
                }
            }
        } else if (config.type === 'section') {
            const item = canvasItems.find(item => item.id === config.itemId);
            if (item && 'type' in item) {
                const section = item as Section;
                currentSettings = {
                    ...(section.properties || {})
                };
            }
        } else if (config.type === 'column-element') {
            const section = canvasItems.find(item => item.id === config.sectionId);
            if (section && 'type' in section) {
                const sectionItem = section as Section;
                let element: Element | undefined;

                if (sectionItem.type === 'single_column' || config.column === 'single') {
                    element = sectionItem.elements.find(el => el.id === config.itemId);
                } else if (sectionItem.type === 'multi_column' && sectionItem.columns && config.column && config.column !== 'single') {
                    element = sectionItem.columns[config.column as 'left' | 'right'].find(el => el.id === config.itemId);
                }

                if (element) {
                    // Check if element has existing properties
                    if (element.properties && Object.keys(element.properties).length > 0) {
                        currentSettings = {
                            content: element.content || '',
                            ...element.properties
                        };
                    } else {
                        // Load default settings from API templates
                        const defaultSettings = getElementDefaultSettings(String(element.type), pageElementsData?.templates);
                        currentSettings = {
                            content: element.content || '',
                            ...defaultSettings
                        };
                    }
                }
            }
        }

        openSettingsPanel(config, currentSettings);
    };

    const isDragging = Boolean(draggedElement || draggedCanvasItem);
    const sectionTypes = ['single_column', 'multi_column'];
    const isDraggingNewElement = Boolean(draggedElement && !sectionTypes.includes(draggedElement));
    const isDraggingExistingElement = draggedCanvasItem?.kind === 'element';
    const isDraggingElement = isDraggingNewElement || isDraggingExistingElement;
    const isDraggingTopLevelInsert = Boolean(
        draggedElement || draggedCanvasItem?.kind === 'section'
    );

    React.useEffect(() => {
        if (isDragging) {
            startAutoScroll();
        } else {
            stopAutoScroll();
        }

        return () => stopAutoScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);

    const createNewElementFromDragged = (): Element | null => {
        if (!draggedElement || sectionTypes.includes(draggedElement)) return null;

        const elementDefaults = createElementWithDefaults(draggedElement, pageElementsData?.templates);
        const runtimeId = createRuntimeElementId(draggedElement);

        return {
            id: runtimeId,
            type: draggedElement as Element['type'],
            content: elementDefaults.content,
            properties: {
                ...(elementDefaults.properties || {}),
                elementClass: createStableElementClass(draggedElement, runtimeId)
            }
        };
    };

    const isPointerInsideCurrentTarget = (e: React.DragEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        return e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    };

    // --- Auto-scroll during drag (supports when cursor leaves or is inside canvas) ---
    const autoScrollState = React.useRef<{ rafId: number | null; velocity: number; container: HTMLElement | null }>({ rafId: null, velocity: 0, container: null });
    const MAX_SPEED = 22; // max px per frame

    const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

    const getContainer = () => {
        return autoScrollState.current.container || (scrollContainerRef && scrollContainerRef.current) || (document.scrollingElement || document.documentElement) as HTMLElement;
    };

    const updateVelocityForPointer = (clientY: number, clientX: number) => {
        const container = getContainer();
        if (!container) return 0;
        autoScrollState.current.container = container;
        const rect = container.getBoundingClientRect();
        const EDGE_MARGIN = Math.min(240, Math.max(120, Math.round(rect.height * 0.25)));

        // allow a bit of extra distance outside the container to keep scrolling
        const topEdge = rect.top + EDGE_MARGIN;
        const bottomEdge = rect.bottom - EDGE_MARGIN;

        let velocity = 0;

        if (clientY < topEdge) {
            const dist = Math.max(0, topEdge - clientY);
            const factor = clamp(dist / EDGE_MARGIN, 0, 1);
            const eased = Math.pow(factor, 1.35);
            velocity = -Math.max(1, Math.ceil(eased * MAX_SPEED));
        } else if (clientY > bottomEdge) {
            const dist = Math.max(0, clientY - bottomEdge);
            const factor = clamp(dist / EDGE_MARGIN, 0, 1);
            const eased = Math.pow(factor, 1.35);
            velocity = Math.max(1, Math.ceil(eased * MAX_SPEED));
        }

        autoScrollState.current.velocity = velocity;
        return velocity;
    };

    const stepAutoScroll = () => {
        const container = autoScrollState.current.container || getContainer();
        if (!container) return;

        const v = autoScrollState.current.velocity;
        if (v !== 0) {
            const maxScroll = container.scrollHeight - container.clientHeight;
            const next = clamp(container.scrollTop + v, 0, maxScroll);
            if (next !== container.scrollTop) container.scrollTop = next;
        }

        autoScrollState.current.rafId = window.requestAnimationFrame(stepAutoScroll);
    };

    const onGlobalDragOver = (e: DragEvent) => {
        const ev: any = e || window.event;
        const clientY = typeof ev.clientY === 'number' ? ev.clientY : 0;
        const clientX = typeof ev.clientX === 'number' ? ev.clientX : 0;
        updateVelocityForPointer(clientY, clientX);
    };

    const startAutoScroll = () => {
        if (autoScrollState.current.rafId !== null) return;
        autoScrollState.current.rafId = window.requestAnimationFrame(stepAutoScroll);
        window.addEventListener('dragover', onGlobalDragOver as any, { passive: true, capture: true });
        window.addEventListener('dragend', stopAutoScroll as any);
        window.addEventListener('drop', stopAutoScroll as any);
    };

    function stopAutoScroll() {
        if (autoScrollState.current.rafId !== null) {
            window.cancelAnimationFrame(autoScrollState.current.rafId);
            autoScrollState.current.rafId = null;
        }
        autoScrollState.current.velocity = 0;
        autoScrollState.current.container = null;
        window.removeEventListener('dragover', onGlobalDragOver as any, true);
        window.removeEventListener('dragend', stopAutoScroll as any);
        window.removeEventListener('drop', stopAutoScroll as any);
    }

    const movePendingChangesForElement = (
        element: Element,
        source: { sectionId: string; column: SectionColumn },
        target: { sectionId: string; column: SectionColumn }
    ) => {
        if (!setPendingChanges) {
            return;
        }

        const sourceKey = `${source.sectionId}-${source.column}-${element.id}`;
        const targetKey = `${target.sectionId}-${target.column}-${element.id}`;

        setPendingChanges(prev => {
            const existingSettings = prev.get(sourceKey) ?? prev.get(element.id);
            if (!existingSettings) {
                return prev;
            }

            const nextChanges = new Map(prev);
            const clonedSettings = clonePendingSettingsWithMeta(existingSettings, {
                itemId: element.id,
                sectionId: target.sectionId,
                column: target.column,
                type: 'column-element',
                isInnerElement: true
            });

            nextChanges.delete(sourceKey);
            nextChanges.delete(element.id);
            nextChanges.set(targetKey, clonedSettings);
            return nextChanges;
        });
    };

    const handleElementDropAtIndex = (
        e: DragEvent<HTMLDivElement>,
        targetSectionId: string,
        targetColumn: SectionColumn,
        targetIndex: number
    ) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isDraggingElement) return;

        let draggedExistingElement: Element | null = null;

        const nextItems = [...canvasItems].map(item => {
            if (draggedCanvasItem?.kind !== 'element') return item;
            if (!('type' in item)) return item;
            if (item.id !== draggedCanvasItem.sectionId) return item;

            const sourceSection = item as Section;

            if (draggedCanvasItem.column === 'single') {
                const sourceElements = [...sourceSection.elements];
                const sourceIndex = sourceElements.findIndex(el => el.id === draggedCanvasItem.elementId);
                if (sourceIndex === -1) return item;
                draggedExistingElement = sourceElements[sourceIndex];
                sourceElements.splice(sourceIndex, 1);
                return { ...sourceSection, elements: sourceElements };
            }

            if (sourceSection.type !== 'multi_column' || !sourceSection.columns) return item;

            const sourceColumnElements = [...sourceSection.columns[draggedCanvasItem.column]];
            const sourceIndex = sourceColumnElements.findIndex(el => el.id === draggedCanvasItem.elementId);
            if (sourceIndex === -1) return item;
            draggedExistingElement = sourceColumnElements[sourceIndex];
            sourceColumnElements.splice(sourceIndex, 1);

            return {
                ...sourceSection,
                columns: {
                    ...sourceSection.columns,
                    [draggedCanvasItem.column]: sourceColumnElements
                }
            };
        });

        const elementToInsert = draggedExistingElement || createNewElementFromDragged();
        if (!elementToInsert) return;

        const withInsertedElement = nextItems.map(item => {
            if (!('type' in item) || item.id !== targetSectionId) return item;

            const targetSection = item as Section;

            if (targetColumn === 'single') {
                const targetElements = [...targetSection.elements];

                let insertionIndex = targetIndex;
                if (
                    draggedCanvasItem?.kind === 'element' &&
                    draggedCanvasItem.sectionId === targetSectionId &&
                    draggedCanvasItem.column === 'single'
                ) {
                    const previousIndex = draggedCanvasItem.sourceIndex;
                    if (previousIndex !== -1 && previousIndex < targetIndex) {
                        insertionIndex = targetIndex - 1;
                    }
                }

                targetElements.splice(insertionIndex, 0, elementToInsert);
                return { ...targetSection, elements: targetElements };
            }

            if (targetSection.type !== 'multi_column' || !targetSection.columns) return item;

            const targetColumnElements = [...targetSection.columns[targetColumn]];
            let insertionIndex = targetIndex;
            if (
                draggedCanvasItem?.kind === 'element' &&
                draggedCanvasItem.sectionId === targetSectionId &&
                draggedCanvasItem.column === targetColumn
            ) {
                const previousIndex = draggedCanvasItem.sourceIndex;
                if (previousIndex !== -1 && previousIndex < targetIndex) {
                    insertionIndex = targetIndex - 1;
                }
            }

            targetColumnElements.splice(insertionIndex, 0, elementToInsert);
            return {
                ...targetSection,
                columns: {
                    ...targetSection.columns,
                    [targetColumn]: targetColumnElements
                }
            };
        });

        setCanvasItems(withInsertedElement);

        if (draggedCanvasItem?.kind === 'element') {
            movePendingChangesForElement(
                elementToInsert,
                { sectionId: draggedCanvasItem.sectionId, column: draggedCanvasItem.column },
                { sectionId: targetSectionId, column: targetColumn }
            );
        }

        setSelectedElement(elementToInsert.id);
        setDraggedElement(null);
        setDraggedCanvasItem(null);
        setActiveElementDropZone(null);
        setActiveInsertIndex(null);
        setIsOverSection(null);
        setIsOverColumn(null);
        setIsOverCanvas(false);
    };

    const handleSectionDragStart = (sectionId: string) => {
        setDraggedCanvasItem({ kind: 'section', sectionId });
        setDraggedElement(null);
        setActiveElementDropZone(null);
    };

    const handleElementDragStart = (sectionId: string, column: SectionColumn, elementId: string) => {
        let sourceIndex = -1;

        const sourceSection = canvasItems.find(
            (item): item is Section => 'type' in item && item.id === sectionId
        );

        if (sourceSection) {
            if (column === 'single') {
                sourceIndex = sourceSection.elements.findIndex(el => el.id === elementId);
            } else if (sourceSection.type === 'multi_column' && sourceSection.columns) {
                sourceIndex = sourceSection.columns[column].findIndex(el => el.id === elementId);
            }
        }

        setDraggedCanvasItem({ kind: 'element', sectionId, column, elementId, sourceIndex });
        setDraggedElement(null);
        setActiveInsertIndex(null);
    };

    const handleExistingItemDragEnd = () => {
        setDraggedCanvasItem(null);
        setActiveInsertIndex(null);
        setActiveElementDropZone(null);
        setIsOverSection(null);
        setIsOverColumn(null);
        setIsOverCanvas(false);
    };

    const renderInsertZone = (index: number, style?: React.CSSProperties) => (
        <InsertDropZone
            theme={theme}
            $isVisible={isDraggingTopLevelInsert}
            $isActive={isDraggingTopLevelInsert && activeInsertIndex === index}
            style={style}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (!isDraggingTopLevelInsert) return;

                if (!isPointerInsideCurrentTarget(e)) {
                    setActiveInsertIndex(null);
                    return;
                }

                setIsOverCanvas(false);
                setActiveElementDropZone(null);
                setActiveInsertIndex(index);
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveInsertIndex(null);
            }}
            onDrop={(e) => handleDropAtIndex(e, index)}
        />
    );

    return (
        <CanvasWrapper theme={theme}>
            <CanvasContent>
                {canvasItems.map((item, index) => {
                    return (
                        <React.Fragment key={item.id}>
                            {renderInsertZone(index)}

                            {'type' in item && (item.type === 'single_column' || item.type === 'multi_column') ? (
                                <SectionContainer
                                    section={item as Section}
                                    selectedElement={selectedElement}
                                    setSelectedElement={setSelectedElement}
                                    isOverSection={isOverSection}
                                    setIsOverSection={setIsOverSection}
                                    isOverColumn={isOverColumn}
                                    setIsOverColumn={setIsOverColumn}
                                    draggedElement={draggedElement}
                                    draggedCanvasItem={draggedCanvasItem}
                                    onSectionDrop={handleSectionDrop}
                                    onColumnDrop={handleColumnDrop}
                                    onSectionDragStart={handleSectionDragStart}
                                    onSectionDragEnd={handleExistingItemDragEnd}
                                    onElementDragStart={handleElementDragStart}
                                    onElementDragEnd={handleExistingItemDragEnd}
                                    onElementDropAtIndex={handleElementDropAtIndex}
                                    activeElementDropZone={activeElementDropZone}
                                    setActiveElementDropZone={setActiveElementDropZone}
                                    onDeleteTopLevel={handleDeleteTopLevel}
                                    onDuplicateSection={handleDuplicateSection}
                                    onDeleteFromSection={handleDeleteFromSection}
                                    onDuplicateFromSection={handleDuplicateFromSection}
                                    onDeleteFromColumn={handleDeleteFromColumn}
                                    onDuplicateFromColumn={handleDuplicateFromColumn}
                                    onOpenSettings={loadElementSettings}
                                    pendingChanges={pendingChanges}
                                    primaryFont={primaryFont}
                                />
                            ) : (
                                // Top-level elements are disabled for now.
                                /*
                                <TopLevelElement
                                    element={item as Element}
                                    selectedElement={selectedElement}
                                    setSelectedElement={setSelectedElement}
                                    onDelete={handleDeleteTopLevel}
                                    onDuplicate={handleDuplicateTopLevel}
                                    onOpenSettings={loadElementSettings}
                                    tempSettings={tempSettings}
                                    pendingChanges={pendingChanges}
                                />
                                */
                                null
                            )}
                        </React.Fragment>
                    );
                })}

                {canvasItems.length === 0 ? (
                    <BottomDropZone
                        theme={theme}
                        isOver={isOverCanvas}
                        onDragOver={(e) => {
                            e.preventDefault();

                            if (!isPointerInsideCurrentTarget(e)) {
                                setIsOverCanvas(false);
                                return;
                            }

                            setActiveInsertIndex(null);
                            setActiveElementDropZone(null);
                            setIsOverCanvas(true);
                        }}
                        onDragLeave={() => {
                            setIsOverCanvas(false);
                        }}
                        onDrop={handleCanvasDrop}
                    >
                        <div className='mb-4'><PageDesignDropzoneIcon /></div>
                        <StyledText fontSize='16px' textColor={theme.CorporateTheme.colors.primary} fontWeight={600}>Start Designing Your Page</StyledText>
                        <StyledText className='mt-1' fontSize='14px' textColor={theme.CorporateTheme.colors.textSecondary} fontWeight={500}>Drag fields from left panel and drop here to add them to your page.</StyledText>
                    </BottomDropZone>
                ) : (
                    renderInsertZone(canvasItems.length, { marginTop: 12, marginBottom: 0 })
                )}
            </CanvasContent>
        </CanvasWrapper>
    );
};

export default Canvas;